/**
 * Built-in trigger apps that are native to Workload.
 * These create a webhook/schedule/form in advance so the user gets a URL or schedule config
 * (triggerMessage with webhookURL, etc.). They require special handling in the UI and in the
 * AI agent so that createTrigger is called to obtain the triggerMessage.
 *
 * Use these appSlug values when generating or modifying workflows so the backend and frontend
 * can apply the correct behavior.
 *
 * Trigger node shape reference: see workflow_examples/webhook_trigger_multi_action_mapped.json
 * (third-party webhook: triggerResource, performList, unsubscribe, webhookKey, endpoint:
 * "$workload_webhookURL"), polling_trigger_single_action_mapped.json (pollingParams,
 * performList, triggerResource), and production_example_2.json (built-in webhook: minimal
 * keys, triggerResource: "", no pollingParams).
 */
export const BUILT_IN_TRIGGER_APPS = {
  SCHEDULE: 'schedule',
  WEBHOOK: 'webhook',
  WORKLOAD_FORMS: 'workload-forms',
};

/** appSlugs that need createTrigger to be called to get triggerMessage (webhook URL, form URL, schedule data). */
export const BUILT_IN_TRIGGER_APPS_NEED_TRIGGER_MESSAGE = [
  BUILT_IN_TRIGGER_APPS.SCHEDULE,
  BUILT_IN_TRIGGER_APPS.WEBHOOK,
  BUILT_IN_TRIGGER_APPS.WORKLOAD_FORMS,
];

/**
 * Helper: true if the trigger app is a built-in that requires triggerMessage from createTrigger.
 */
export function isBuiltInTriggerApp(appSlug) {
  return BUILT_IN_TRIGGER_APPS_NEED_TRIGGER_MESSAGE.includes(appSlug);
}

/**
 * List action sent to getDD for agent polling triggers.
 * formData shape: trigger key (e.g. newCompletedJob) has separate `action` (e.g. jobCompleted).
 * Never use trigger_key / type for polling getDD when `action` is available.
 */
export function resolveAgentTriggerPerformList(trigger) {
  if (!trigger || typeof trigger !== 'object') return '';
  const explicit =
    (trigger.performList && String(trigger.performList).trim()) ||
    (trigger.trigger_action && String(trigger.trigger_action).trim()) ||
    (trigger.triggerResource && String(trigger.triggerResource).trim());
  if (explicit) return explicit;
  if (trigger.event_type === 'polling') return '';
  return String(trigger.trigger_key || trigger.type || '').trim();
}

/** True when the agent trigger is a third-party polling trigger (e.g. ServiceTitan new completed job). */
export function isAgentPollingTrigger(trigger) {
  if (!trigger || typeof trigger !== 'object') return false;
  const eventType = String(trigger.event_type || '').toLowerCase();
  if (eventType === 'polling') return true;
  if (String(trigger.source || '').toLowerCase() === 'polling') return true;
  const app = String(trigger.app || trigger.app_label || '').toLowerCase().trim();
  if (app && isBuiltInTriggerApp(app)) return false;
  const performList = resolveAgentTriggerPerformList(trigger);
  const pollingParams = trigger.pollingParams;
  if (
    performList &&
    pollingParams &&
    typeof pollingParams === 'object' &&
    Object.keys(pollingParams).length > 0
  ) {
    return true;
  }
  return false;
}

/** True when starting the agent must call createTrigger (schedule, built-in webhook/forms, polling). */
export function agentTriggerNeedsCreateTrigger(trigger) {
  if (!trigger || typeof trigger !== 'object' || trigger.enabled === false) return false;
  const app = String(trigger.app || trigger.app_label || '').toLowerCase().trim();
  if (app && isBuiltInTriggerApp(app)) return true;
  return isAgentPollingTrigger(trigger);
}

/** Correct API base for built-in apps (webhook, schedule, workload-forms). Other apps use odkusrnpx8. */
export const BUILT_IN_APPS_BASE_URL = 'https://7nx4ewphyg.execute-api.us-west-2.amazonaws.com/production/apps';
const WRONG_APPS_BASE = 'https://odkusrnpx8.execute-api.us-west-2.amazonaws.com/production/apps';

/**
 * FormDataEndpoint for built-in trigger apps. Use this when the app is webhook, schedule, or workload-forms.
 */
export function getBuiltInAppFormDataEndpoint(appSlug) {
  if (!appSlug || !isBuiltInTriggerApp(appSlug)) return null;
  return `${BUILT_IN_APPS_BASE_URL}/${appSlug}`;
}

/**
 * Normalize node URL: if app is a built-in (webhook/schedule/workload-forms) and url points at the wrong
 * API (odkusrnpx8), return the correct URL so trigger data and getDD work.
 */
export function normalizeBuiltInAppUrl(appSlug, url) {
  if (!url || typeof url !== 'string') return url;
  if (!appSlug || !isBuiltInTriggerApp(appSlug)) return url;
  if (url.startsWith(WRONG_APPS_BASE)) return getBuiltInAppFormDataEndpoint(appSlug);
  return url;
}

/** Default trigger key and triggerResource for built-in webhook (formDataEndpoint shape). */
const WEBHOOK_DEFAULT_TRIGGER_KEY = 'webhookReceived';
const WEBHOOK_DEFAULT_TRIGGER_RESOURCE = 'createWebhook';

/**
 * Normalize built-in app URLs and trigger keys in frontend workflow elements (e.g. after load or agent apply).
 * - Replaces odkusrnpx8 URLs for webhook/schedule/workload-forms with the correct 7nx4ewphyg URL.
 * - For webhook trigger nodes with missing action/triggerKey, sets action=webhookReceived and triggerResource=createWebhook
 *   so the canvas and panel show the real node and the user can complete setup (createTrigger) in manual mode.
 */
export function normalizeBuiltInAppUrlsInElements(elements) {
  if (!Array.isArray(elements) || elements.length === 0) return elements;
  return elements.map((el) => {
    if (!el || !el.id || el.source != null || el.target != null) return el;
    const app = el.data?.app;
    const url = el.data?.url;
    let data = { ...el.data };
    const normalizedUrl = normalizeBuiltInAppUrl(app, url);
    if (normalizedUrl !== url) data = { ...data, url: normalizedUrl };
    if (app === BUILT_IN_TRIGGER_APPS.WEBHOOK) {
      // Always use correct keys for built-in webhook (triggerKey=webhookReceived, triggerResource=createWebhook)
      data = {
        ...data,
        action: data.action && String(data.action).trim() ? data.action : WEBHOOK_DEFAULT_TRIGGER_KEY,
        triggerKey: WEBHOOK_DEFAULT_TRIGGER_KEY,
        triggerResource: WEBHOOK_DEFAULT_TRIGGER_RESOURCE,
        type: 'webhook',
      };
    }
    if (data === el.data) return el;
    return { ...el, data };
  });
}

/**
 * Trigger node keys by trigger type (reference: workflow_examples/webhook_trigger_multi_action_mapped.json
 * vs polling_trigger_single_action_mapped.json vs production_example_2.json).
 *
 * --- POLLING ONLY (type === 'polling') ---
 * - pollingParams: { dedupe_key, data_head } — required for polling; used for deduplication and list path.
 * - performList / triggerResource: the list action (e.g. "lastRow", "listOpportunities"). Do NOT set for
 *   built-in schedule/webhook/workload-forms.
 *
 * --- THIRD-PARTY WEBHOOK (type === 'webhook' but app !== 'webhook', e.g. ClickUp, Slack) ---
 * - triggerResource: "createWebhook" (or similar) — action that registers the webhook with the external app.
 * - performList: list action used for dropdowns (e.g. "listFolders").
 * - unsubscribe: action to delete the webhook (e.g. "deleteWebhook").
 * - webhookKey: key in the incoming payload that identifies the resource (e.g. "folder_id").
 * - fields: must include a field whose value is "endpoint": "$workload_webhookURL" (replaced with
 *   triggerMessage['Webhook URL'] when creating the webhook).
 * - pollingParams: often {} (empty); not used.
 * - triggerMessage: may be "" until created; after createTrigger it may hold webhook id/URL as needed.
 *
 * --- BUILT-IN APPS (app in schedule | webhook | workload-forms) ---
 * Populate:
 * - type: 'webhook' for app webhook; app schedule/workload-forms use their own type as returned by app API.
 * - action: trigger key from formDataEndpoint (e.g. "webhookReceived" for webhook) — the event that fires.
 * - triggerResource: for webhook, the trigger's .action from formDataEndpoint (e.g. "createWebhook"); "" for schedule/workload-forms.
 * - url: app endpoint (e.g. production/apps/webhook, production/apps/workload-forms).
 * - triggerMessage: MUST be populated by createTrigger (contains "Webhook URL" for webhook, form URL or
 *   schedule data for workload-forms/schedule). Do not leave as "" when generating; backend must call
 *   createTrigger or frontend will when the panel opens.
 * - fields: optional; for schedule (schedule config), for workload-forms (form title, questions).
 *
 * Omit or leave empty (do NOT copy from polling or third-party webhook examples):
 * - pollingParams — polling only; omit or {} for built-in.
 * - performList — polling / third-party webhook list action; omit or "" for built-in (no getDD).
 * - unsubscribe — third-party webhook tear-down; omit for built-in.
 * - webhookKey — third-party webhook payload key; omit for built-in.
 * - No field with "endpoint": "$workload_webhookURL" — that is for registering Workload's URL with an
 *   external app; built-in webhook/workload-forms receive on Workload's own URL.
 */

/**
 * AI/Backend guidance for configuring these triggers from natural language:
 *
 * - Schedule (appSlug: 'schedule')
 *   User examples: "Every day at 12pm", "Every Monday at 9am", "Run every hour"
 *   Set trigger node: app: 'schedule', label: 'Schedule'. Trigger step fields define the schedule
 *   (e.g. cron or preset). After createTrigger, triggerMessage holds schedule data and testData can use it.
 *   Do not set pollingParams, performList, unsubscribe, webhookKey.
 *
 * - Webhook (appSlug: 'webhook')
 *   User examples: "When another app sends a webhook", "Trigger from an external POST"
 *   Set trigger node: app: 'webhook', type: 'webhook', label: 'Webhook', triggerResource: "".
 *   createTrigger returns triggerMessage with 'Webhook URL'. Omit pollingParams, performList,
 *   unsubscribe, webhookKey; fields can be [].
 *
 * - Forms by Workload (appSlug: 'workload-forms')
 *   User examples: "A form titled 'GAM Trafficking' with questions about launching an ad campaign
 *   on Google Ad Manager", "A form that collects name, email, and campaign name"
 *   Set trigger node: app: 'workload-forms', label: 'Forms by Workload'. Optionally set fields
 *   to define form title and questions. createTrigger returns triggerMessage with form URL.
 *   Omit pollingParams, performList, unsubscribe, webhookKey.
 */
